import type { FC } from 'react';
import React from 'react';
import { Grid } from '@alfa-bank/site-grid-universal';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { BlockDesktop as Block } from '@alfa-bank/site-block/desktop';
import { BackgroundV2 as Background } from '@alfa-bank/site-background-v2';
import { LogoDesktop as Logo } from '@alfa-bank/site-invest-emitents-common/components/logo-desktop';
import {
    getAssetNameByType,
    getTickerOrIsin,
} from '@alfa-bank/site-invest-emitents-common/helpers';
import cn from 'classnames';

import { defaultDividendBannerTexts } from '../../configs';
import { OpenAccountButtonDesktop as OpenAccountButton } from '../open-account-button/desktop';
import { DividendValue } from '../dividend-cells/dividend-value/component';
import { PayDateDesktop as PayDate } from '../dividend-cells/pay-date/desktop';
import { ProfitabilityPercentDesktop as ProfitabilityPercent } from '../dividend-cells/profitability-percent/desktop';
import { getDividendsInfo } from '../../helpers';

import type { IDividendBannerProps } from './models';
import styles from './desktop.css';

export const DividendBannerDesktop: FC<IDividendBannerProps> = ({
    analytics,
    anchorLink,
    asset,
    backgroundColor,
    backgroundImage,
    buttonText,
    payDateTooltipHint,
    paymentType,
    title = defaultDividendBannerTexts.title.desktop,
}) => {
    const { assetType, issuer } = asset;
    const tickerOrIsin = getTickerOrIsin(asset);
    const assetName = getAssetNameByType(assetType.code, asset);
    const { lastDateLong } = getDividendsInfo(asset);

    return (
        <Background color={backgroundColor} image={backgroundImage} styler={{ borderRadius: 24 }}>
            <Block
                className={cn(styles.external, styles.banner)}
                notPadded
                styler={{ borderRadius: 24, padding: 32 }}
            >
                <Grid
                    alignment="center"
                    collapse
                    columnsWidth={[80, null, 157]}
                    styler={{ marginBottom: 32 }}
                >
                    <Logo assetName={assetName} issuer={issuer} size={64} />
                    <Block notPadded>
                        <Grid
                            colAutoWidth
                            columnsMargin={8}
                            justify="start"
                            styler={{ marginBottom: 4 }}
                        >
                            <Text
                                className={cn(styles.external, styles.paymentType)}
                                styler={{ margin: 0 }}
                                tag="span"
                                view="accent-secondary-small"
                            >
                                {paymentType}
                            </Text>
                            <Text
                                className={cn(styles.external, styles.label)}
                                styler={{ margin: 0, paddingBottom: 2, paddingTop: 2 }}
                                tag="span"
                                view="accent-secondary-small"
                            >
                                {tickerOrIsin}
                            </Text>
                        </Grid>
                        <Text styler={{ marginTop: 0, marginBottom: 0 }} view="medium">
                            {title} {lastDateLong}
                        </Text>
                    </Block>
                    <OpenAccountButton
                        analytics={analytics}
                        anchorLink={anchorLink}
                        buttonText={buttonText}
                        styler={{ borderRadius: 12 }}
                    />
                </Grid>

                <Grid
                    alignment="center"
                    className={cn(styles.external, styles.cells)}
                    colAutoWidth
                    collapse
                    columnsMargin={48}
                    justify="start"
                >
                    <ProfitabilityPercent asset={asset} />
                    <DividendValue asset={asset} />
                    <PayDate asset={asset} tooltipHint={payDateTooltipHint} />
                </Grid>
            </Block>
        </Background>
    );
};
